import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton,
  InputAdornment,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Brightness7 as Brightness7Icon,
  Brightness4 as Brightness4Icon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiUrl';

import { useAuth } from '../../contexts/AuthContext';
import SuccessDialog from '../../components/SuccessDialog';
import MetaTags from '../../components/MetaTags';
import { useSharedTheme } from '../../theme/useSharedTheme';
import './ClientLogin-light.css';
import './ClientLogin-dark.css';

const validationSchema = yup.object({
  usernameOrEmail: yup
    .string()
    .required('Username or email is required'),
  password: yup
    .string()
    .min(8, 'Password should be of minimum 8 characters length')
    .required('Password is required'),
});

const ClientLogin: React.FC = () => {
  const { companySlug } = useParams<{ companySlug: string }>();
  const { login } = useAuth();
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { isDarkMode, toggleTheme, theme } = useSharedTheme();
  const [totalLeads, setTotalLeads] = useState(0);
  const [successRate, setSuccessRate] = useState(0);
  const [activeClients, setActiveClients] = useState(0);
  const [barAnimationKey, setBarAnimationKey] = useState(0);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });

  // Animate counters on component mount
  useEffect(() => {
    const animateCounter = (setter: (value: number) => void, target: number, duration: number) => {
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      let stepCount = 0;
      const intervalTime = duration / steps;

      const interval = setInterval(() => {
        stepCount++;
        current += increment;
        setter(Math.floor(current));

        if (stepCount >= steps) {
          setter(target);
          clearInterval(interval);
        }
      }, intervalTime);
    };

    // Start animations with delays
    setTimeout(() => animateCounter(setTotalLeads, 2400, 2000), 500);
    setTimeout(() => animateCounter(setSuccessRate, 89, 2000), 700);
    setTimeout(() => animateCounter(setActiveClients, 42, 2000), 900);
  }, []);

  // Trigger bar animation when theme changes
  useEffect(() => {
    setBarAnimationKey(prev => prev + 1);
  }, [isDarkMode]);

  const formik = useFormik({
    initialValues: {
      usernameOrEmail: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        // Determine if input is email or username and send appropriate field
        const isEmail = values.usernameOrEmail.includes('@');
        const loginData = {
          password: values.password,
          companySlug: companySlug,
          ...(isEmail ? { email: values.usernameOrEmail } : { username: values.usernameOrEmail })
        };
        
        const response = await axios.post(`${getApiUrl()}/api/auth/client/login`, loginData);
        if (response.data.token) {
          // Show success dialog first
          setShowSuccessDialog(true);
          setSuccessDialogConfig({
            title: 'Login Successful',
            message: 'Welcome back! Redirecting to your dashboard...',
            autoCloseDelay: 3000,
          });
          
          // Delay the login to allow dialog to show
          setTimeout(() => {
            login(response.data.token);
          }, 2000); // 2 second delay to show the dialog properly
        }
      } catch (error: any) {
        console.error('Login error:', error);
        
        // Handle multiple accounts with same email
        if (error.response?.data?.multipleAccounts && error.response?.data?.useUsername) {
          setSuccessDialogConfig({
            title: 'Multiple Accounts Detected',
            message: 'This email is associated with multiple accounts in your company. Please login using your username instead of email for security purposes.',
            autoCloseDelay: 10000,
          });
          setShowSuccessDialog(true);
        } else {
          setSuccessDialogConfig({
            title: 'Login Failed',
            message: error.response?.data?.message || 'Login failed. Please check your credentials.',
            autoCloseDelay: 3000,
          });
          setShowSuccessDialog(true);
        }
      }
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <MetaTags 
        title={`${companySlug} Client Login - LeadForge`}
        description={`Secure client login for ${companySlug}. Access your company dashboard and analytics.`}
        keywords={`${companySlug} login, LeadForge, client dashboard, company analytics`}
      />
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          height: { xs: 'auto', md: '100vh' },
          overflow: { xs: 'auto', md: 'hidden' },
          background: { 
            xs: 'transparent', 
            md: isDarkMode 
              ? theme.palette.gradients.authDark
              : theme.palette.gradients.authLight
          },
          transition: 'background 0.5s ease-in-out',
          position: 'relative',
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {/* Centered Form Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: { xs: 'start', md: 'center' },
            flex: { xs: 'none', md: 1 },
            width: '100%',
            minHeight: '100vh',
            height: { xs: 'auto', md: '100vh' },
            padding: { xs: 0, md: 4 },
            paddingTop: { xs: 0, md: 4 },
          }}
        >
          <Box 
            className={isDarkMode ? 'client-login-card-dark' : ''}
            sx={{
              width: { xs: '100%', md: '80%' }, 
              maxWidth: { xs: 'none', md: 1000 }, 
              backgroundColor: isDarkMode ? '#191919' : '#ffffff',
              border: { xs: 'none', md: '0.3px solid #B9B9B9' },
              borderRadius: { xs: 0, md: 4 },
              padding: { xs: '10px', md: 4 },
              marginBottom: { xs: 0, md: 0 },
              boxShadow: { xs: 'none', md: '0 20px 40px rgba(0, 0, 0, 0.1)' },
              position: 'relative',
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: { xs: 'start', md: 'center' },
              alignItems: 'center',
              flex: { xs: 'none', md: 1 },
              minHeight: { xs: '100vh', md: '670px' },
              transition: 'background-color 0.3s ease',
            }}
          >
            {/* Theme Toggle - Top Right Corner of White Container */}
            <Box
              sx={{
                position: 'absolute',
                top: '2%',
                right: '1%',
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                backgroundColor: 'transparent',
                border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                borderRadius: 20,
                padding: { xs: 1.5, md: 2 },
                width: { xs: 60, md: 80 },
                height: { xs: 32, md: 40 },
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              onClick={toggleTheme}
            >
              {/* Sun Icon - Left Side */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 3, md: 4 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 20, md: 24 },
                  height: { xs: 20, md: 24 },
                  zIndex: 2,
                }}
              >
                <img
                  src="/super-admin/sun-icon.svg"
                  alt="Light Mode"
                  style={{
                    width: '100%',
                    height: '100%',
                    opacity: isDarkMode ? 0.5 : 1,
                    transition: 'opacity 0.3s ease',
                    filter: isDarkMode ? 'none' : 'brightness(0) saturate(100%) invert(33%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
                  }}
                />
              </Box>
              
              {/* Moon Icon - Right Side */}
              <Box
                sx={{
                  position: 'absolute',
                  right: { xs: 3, md: 4 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 20, md: 24 },
                  height: { xs: 20, md: 24 },
                  zIndex: 2,
                }}
              >
                <img
                  src="/super-admin/moon-icon.svg"
                  alt="Dark Mode"
                  style={{
                    width: '100%',
                    height: '100%',
                    opacity: !isDarkMode ? 0.5 : 1,
                    transition: 'opacity 0.3s ease',
                    filter: isDarkMode ? 'brightness(0) invert(1)' : 'none'
                  }}
                />
              </Box>
              
              {/* Toggle Switch */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: isDarkMode ? { xs: 2, md: 4 } : { xs: 30, md: 44 },
                  width: { xs: 28, md: 32 },
                  height: { xs: 28, md: 32 },
                  background: isDarkMode ? '#ffffff' : '#1B4672',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'left 0.3s ease, background 0.3s ease',
                  zIndex: 3,
                }}
              />
            </Box>

            {/* Left Column - Analytics Content - Hidden on Mobile */}
            <Box className="client-left-column">
              {/* Logo */}
              <Box className="client-logo-container">
                <img
                  src={isDarkMode ? "/super-admin/leadforge-dark-logo.svg" : "/super-admin/leadforge-light-logo.svg"}
                  alt="LeadForge"
                  className="client-logo"
                />
              </Box>

              {/* Description */}
              <Typography
                variant="h6"
                className={isDarkMode ? "client-login-description-dark" : "client-login-description-light"}
              >
                Access your company dashboard and analytics tools.
              </Typography>

               {/* Analytics Overview */}
               <Typography
                 variant="h5"
                 sx={{
                   color: isDarkMode ? '#ffffff' : '#666666',
                   fontWeight: 600,
                   mb: 3,
                   mt: 4,
                   textAlign: 'left',
                   fontSize: '34px',
                   letterSpacing: '-0.11px',
                   opacity: 1,
                 }}
               >
                Analytics Overview
              </Typography>

              {/* Bar Chart */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  height: '150px',
                  width: '300px',
                  mb: 4,
                  '& .bar': {
                    width: '27px',
                    background: isDarkMode 
                      ? '#ffffff' 
                      : '#1B4672',
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    height: 'var(--final-height)',
                    transition: 'background 0.5s ease, transform 0.3s ease, height 0.4s ease',
                    cursor: 'pointer',
                    '&.bar-1': { animationDelay: '0s' },
                    '&.bar-2': { animationDelay: '0.1s' },
                    '&.bar-3': { animationDelay: '0.2s' },
                    '&.bar-4': { animationDelay: '0.3s' },
                    '&.bar-5': { animationDelay: '0.4s' },
                    '&.bar-6': { animationDelay: '0.5s' },
                    '&.bar-7': { animationDelay: '0.6s' },
                    '&:hover': {
                      transform: 'scaleY(1.1)',
                    },
                    '&.initial-grow': {
                      height: 0,
                      animation: 'growBar 1.5s ease forwards',
                    },
                    '&.theme-transition': {
                      animation: 'themeTransition 1.2s ease-in-out',
                    },
                  },
                  '@keyframes growBar': {
                    '0%': { 
                      height: 0,
                      transform: 'scaleY(0.8)',
                    },
                    '50%': {
                      height: 'var(--final-height)',
                      transform: 'scaleY(0.9)',
                    },
                    '100%': { 
                      height: 'var(--final-height)',
                      transform: 'scaleY(1)',
                    },
                  },
                  '@keyframes themeTransition': {
                    '0%': { 
                      height: 'var(--final-height)',
                      transform: 'scaleY(1)',
                    },
                    '50%': {
                      height: 'calc(var(--final-height) * 0.2)',
                      transform: 'scaleY(0.6)',
                    },
                    '100%': { 
                      height: 'var(--final-height)',
                      transform: 'scaleY(1)',
                    },
                  },
                }}
              >
                {[100, 80, 64, 95, 85, 75, 72].map((height, index) => (
                  <Box
                    key={`${barAnimationKey}-${index}`}
                    className={`bar bar-${index + 1} ${barAnimationKey === 0 ? 'initial-grow' : 'theme-transition'}`}
                    sx={{
                      '--final-height': `${height}%`,
                    }}
                  />
                ))}
              </Box>

              {/* Counters */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '300px',
                  textAlign: 'left',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#202224',
                      fontWeight: 'bold',
                      fontSize: '30px',
                      letterSpacing: '-0.11px',
                      opacity: 1,
                    }}
                  >
                    {(totalLeads / 1000).toFixed(1)}K
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#666666',
                      fontSize: '12px',
                      letterSpacing: '-0.11px',
                    }}
                  >
                    Total Leads
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#202224',
                      fontWeight: 'bold',
                      fontSize: '30px',
                      letterSpacing: '-0.11px',
                      opacity: 1,
                    }}
                  >
                    {successRate}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#666666',
                      fontSize: '12px',
                      letterSpacing: '-0.11px',
                    }}
                  >
                    Success Rate
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#202224',
                      fontWeight: 'bold',
                      fontSize: '30px',
                      letterSpacing: '-0.11px',
                      opacity: 1,
                    }}
                  >
                    {activeClients}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#666666',
                      fontSize: '12px',
                      letterSpacing: '-0.11px',
                    }}
                  >
                    Active Clients
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Column - Login Form */}
            <Box
              sx={{
                width: { xs: '100%', md: '48%' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'start', md: 'center' },
                alignItems: 'center',
                flex: { xs: 'none', md: 1 },
                paddingTop: { xs: '60px', md: 4 },
                paddingBottom: { xs: '10px', md: 4 },
                paddingLeft: { xs: '10px', md: 4 },
                paddingRight: { xs: '10px', md: 4 },
                position: 'relative',
              }}
            >
              {/* Mobile Header Row - Logo Left, Theme Toggle Right */}
              <Box sx={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                display: { xs: 'flex', md: 'none' }, 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '10px 20px',
                width: '100%',
                zIndex: 1000,
                backgroundColor: 'transparent'
              }}>
                {/* Mobile Logo */}
                <Box sx={{ textAlign: 'left' }}>
                  <img
                    src={isDarkMode ? "/super-admin/leadforge-dark-logo.svg" : "/super-admin/leadforge-light-logo.svg"}
                    alt="LeadForge"
                    style={{
                      height: '35px',
                      width: 'auto',
                      objectFit: 'contain',
                      transition: 'opacity 0.3s ease',
                    }}
                  />
                </Box>

                {/* Mobile Theme Toggle */}
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'transparent',
                    border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                    borderRadius: 20,
                    padding: 1.5,
                    width: 60,
                    height: 32,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                  onClick={toggleTheme}
                >
                  {/* Sun Icon - Left Side */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 3,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      zIndex: 2,
                    }}
                  >
                    <Brightness7Icon 
                      sx={{ 
                        fontSize: 16,
                        color: isDarkMode ? '#ffffff' : '#000000',
                        transition: 'all 0.3s ease',
                        opacity: isDarkMode ? 0.3 : 1,
                      }} 
                    />
                  </Box>

                  {/* Moon Icon - Right Side */}
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 3,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      zIndex: 2,
                    }}
                  >
                    <Brightness4Icon 
                      sx={{ 
                        fontSize: 16,
                        color: isDarkMode ? '#ffffff' : '#000000',
                        transition: 'all 0.3s ease',
                        opacity: isDarkMode ? 1 : 0.3,
                      }} 
                    />
                  </Box>

                  {/* Toggle Circle */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: isDarkMode ? 33 : 3,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 20,
                      height: 20,
                      backgroundColor: isDarkMode ? '#ffffff' : '#000000',
                      borderRadius: '50%',
                      transition: 'all 0.3s ease',
                      zIndex: 1,
                    }}
                  />
                </Box>
              </Box>

              {/* Login Form */}
              <Box sx={{ width: '100%', maxWidth: '400px' }}>
                 <Typography
                   variant="h4"
                   sx={{
                     fontWeight: 700,
                     color: isDarkMode ? '#ffffff' : '#202224',
                     mb: 1,
                     textAlign: { xs: 'left', md: 'center' },
                     fontSize: { xs: '1.5rem', md: '1.5rem' },
                     letterSpacing: '-0.11px',
                   }}
                 >
                   Client Login
                 </Typography>
                 
                 <Typography
                   variant="body1"
                   sx={{
                     color: isDarkMode ? '#ffffff' : '#666666',
                     mb: 4,
                     textAlign: { xs: 'left', md: 'center' },
                     fontSize: { xs: '0.9rem', md: '16px' },
                     letterSpacing: '-0.06px',
                     opacity: 0.8,
                   }}
                 >
                  <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                    Please enter your email and password
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                    Please enter your email and password
                  </Box>
                </Typography>

                <form onSubmit={formik.handleSubmit}>
                  {/* Email Field */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: isDarkMode ? '#ffffff' : '#202224',
                        mb: 1,
                        fontSize: '0.95rem',
                      }}
                    >
                      Email address:
                    </Typography>
                    <TextField
                      fullWidth
                      id="usernameOrEmail"
                      name="usernameOrEmail"
                      placeholder="Email@domain.com"
                      value={formik.values.usernameOrEmail}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.usernameOrEmail && Boolean(formik.errors.usernameOrEmail)}
                      helperText={formik.touched.usernameOrEmail && formik.errors.usernameOrEmail}
                      className={isDarkMode ? 'client-login-input-dark' : 'client-login-input-light'}
                    />
                  </Box>

                  {/* Password Field */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: isDarkMode ? '#ffffff' : '#202224',
                          fontSize: '0.95rem',
                        }}
                      >
                        Password
                      </Typography>
                      <Link
                        href={`/${companySlug}/password-reset`}
                        sx={{
                          color: isDarkMode ? '#ffffff' : '#666666',
                          textDecoration: 'none',
                          fontSize: '0.85rem',
                          fontWeight: 400,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Forget Password?
                      </Link>
                    </Box>
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="●●●●●●●●"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              sx={{ 
                                color: '#A6A6A6',
                                backgroundColor: 'transparent',
                                borderRadius: '0 30px 30px 0',
                                marginRight: '-1px',
                                marginTop: '-1px',
                                marginBottom: '-1px',
                                height: '48px',
                                width: '48px',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                }
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      className={isDarkMode ? 'client-login-input-dark' : 'client-login-input-light'}
                    />
                  </Box>

                  {/* Remember Me Checkbox */}
                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          sx={{
                            color: isDarkMode ? '#ffffff' : '#1B4672',
                            '&.Mui-checked': {
                              color: isDarkMode ? '#ffffff' : '#1B4672',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color: isDarkMode ? '#ffffff' : '#202224',
                            fontSize: '0.9rem',
                          }}
                        >
                          Remember me
                        </Typography>
                      }
                    />
                  </Box>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      background: isDarkMode 
                        ? '#ffffff' 
                        : '#1B4672',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: isDarkMode ? '#1B4672' : 'white',
                      '&:hover': {
                        background: isDarkMode 
                          ? '#f0f0f0' 
                          : '#1B4672',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    Sign In
                  </Button>
                </form>


                 {/* Request Access Notice */}
                 <Typography
                   variant="body2"
                   sx={{
                     textAlign: 'center',
                     mt: 5,
                     pb: 2,
                     color: isDarkMode ? '#ffffff' : '#666666',
                     fontSize: '0.85rem',
                   }}
                 >
                   Don't have an account?{' '}
                   <Box
                     component="span"
                     onClick={() => {
                       // Save company slug to sessionStorage so ClientSignup can use it
                       if (companySlug) {
                         sessionStorage.setItem('companySlug', companySlug);
                         // Also save to localStorage as backup since sessionStorage gets cleared
                         localStorage.setItem('companySlug', companySlug);
                       }
                       // Set flag to allow access to signup page
                       sessionStorage.setItem('explicitSignupAccess', 'true');
                       // Navigate to client signup
                       window.location.href = '/client-signup';
                     }}
                     sx={{
                       color: isDarkMode ? '#ffffff' : '#1B4672',
                       textDecoration: 'underline',
                       fontWeight: 500,
                       fontSize: '16px',
                       cursor: 'pointer',
                       '&:hover': {
                         color: isDarkMode ? '#ffffff' : '#1B4672',
                       },
                     }}
                   >
                     Request Access
                   </Box>
                 </Typography>

                 {/* Single Session Notice */}
                 <Typography
                   variant="body2"
                   sx={{
                     textAlign: 'center',
                     mt: 3,
                     color: isDarkMode ? '#ffffff' : '#666666',
                     fontSize: '10px',
                     lineHeight: 1.4,
                     maxWidth: '90%',
                     margin: '0 auto 0',
                   }}
                 >
                  Single Session - One active session per account.
                  <br />
                  Use incognito for multiple logins.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </ThemeProvider>
  );
};

export default ClientLogin;
